/**
 * Security Controls — admin-section learning surface.
 *
 * Mounted under /admin/security-controls so it sits next to the SCF
 * Dataset tab (which controls the data this page reads). Browsing is
 * cross-tenant: every signed-in admin can see the full SCF catalog,
 * filter by jurisdiction or framework, search by control text, and
 * drill into a single control to see its full crosswalk.
 *
 * Distinct from the org's Compliance Posture page (which overlays
 * findings + pass/fail state). This is the dictionary; that's the
 * report card.
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ShieldCheck, Search, X, Loader2, ChevronRight, ChevronDown, ExternalLink } from 'lucide-react';
import { apiClient } from '@/lib/api-client';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { AdminShell } from './admin-shell';

interface FrameworkRow {
  frameworkId: string;
  name: string;
  shortName: string;
  jurisdiction: string;
  category: 'standard' | 'regulation' | 'industry';
  description: string | null;
  url: string | null;
}

interface Control {
  scfId: string;
  version: string;
  domain: string;
  name: string;
  description: string;
}

interface CrosswalkEntry {
  frameworkId: string;
  shortName: string;
  name: string;
  jurisdiction: string;
  category: string;
  clauses: Array<{ clauseId: string; clauseTitle: string | null; relationship: string }>;
}

const JURISDICTION_LABELS: Record<string, string> = {
  global: 'Global', us: 'United States', eu: 'European Union', uk: 'United Kingdom',
  at: 'Austria', be: 'Belgium', de: 'Germany', gr: 'Greece', hu: 'Hungary',
  ie: 'Ireland', il: 'Israel', it: 'Italy', ke: 'Kenya', ng: 'Nigeria',
  no: 'Norway', pl: 'Poland', qa: 'Qatar', ru: 'Russia', sa: 'Saudi Arabia',
  rs: 'Serbia', za: 'South Africa', es: 'Spain', ch: 'Switzerland',
  tr: 'Turkey', ae: 'United Arab Emirates',
  au: 'Australia', cn: 'China', hk: 'Hong Kong', in: 'India', jp: 'Japan',
  my: 'Malaysia', nz: 'New Zealand', ph: 'Philippines', sg: 'Singapore',
  kr: 'South Korea', tw: 'Taiwan',
  ar: 'Argentina', bs: 'Bahamas', bm: 'Bermuda', br: 'Brazil', ca: 'Canada',
  cl: 'Chile', co: 'Colombia', mx: 'Mexico',
};

const RELATIONSHIP_LABEL: Record<string, string> = {
  equal: 'Equivalent',
  subset: 'Subset of',
  superset: 'Superset of',
  intersects: 'Intersects',
};

export function SecurityControlsPage() {
  const [jurisdictions, setJurisdictions] = useState<string[]>([]);
  const [frameworks, setFrameworks] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  // Per-filter search boxes — let users find a specific jurisdiction
  // or framework chip without scrolling through the entire list.
  const [jSearch, setJSearch] = useState('');
  const [fSearch, setFSearch] = useState('');
  const normJSearch = jSearch.trim().toLowerCase();
  const normFSearch = fSearch.trim().toLowerCase();
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const { data: fwRes } = useQuery({
    queryKey: ['scf-catalog', 'frameworks'],
    queryFn: () => apiClient.listScfCatalogFrameworks(),
    staleTime: 5 * 60_000,
  });
  const allFrameworks = (fwRes?.data as FrameworkRow[] | undefined) ?? [];

  const { data: ctrlRes, isLoading } = useQuery({
    queryKey: ['scf-catalog', 'controls', jurisdictions.join(','), frameworks.join(','), search],
    queryFn: () => apiClient.listScfCatalogControls({
      jurisdictions: jurisdictions.length > 0 ? jurisdictions : undefined,
      frameworks: frameworks.length > 0 ? frameworks : undefined,
      search: search.trim() || undefined,
      limit: 200,
    }),
    staleTime: 60_000,
  });
  const controls = (ctrlRes?.data as Control[] | undefined) ?? [];
  const total = (ctrlRes?.meta as { total?: number; version?: string } | undefined)?.total ?? 0;
  const version = (ctrlRes?.meta as { total?: number; version?: string } | undefined)?.version;

  // Available jurisdictions = union from the framework catalog.
  const availableJurisdictions = useMemo(() => {
    const set = new Set<string>();
    for (const f of allFrameworks) if (f.jurisdiction) set.add(f.jurisdiction);
    return Array.from(set).sort((a, b) =>
      (JURISDICTION_LABELS[a] ?? a).localeCompare(JURISDICTION_LABELS[b] ?? b),
    );
  }, [allFrameworks]);

  // Visible jurisdictions honour the jurisdiction-search box.
  const visibleJurisdictions = useMemo(() => {
    if (!normJSearch) return availableJurisdictions;
    return availableJurisdictions.filter((j) =>
      (JURISDICTION_LABELS[j] ?? j).toLowerCase().includes(normJSearch) || j.toLowerCase().includes(normJSearch),
    );
  }, [availableJurisdictions, normJSearch]);

  // Frameworks visible in the picker honour the jurisdiction filter
  // AND the framework-search box.
  const visibleFrameworks = useMemo(() => {
    let list = allFrameworks;
    if (jurisdictions.length > 0) list = list.filter((f) => jurisdictions.includes(f.jurisdiction));
    if (normFSearch) {
      list = list.filter((f) =>
        f.name.toLowerCase().includes(normFSearch) ||
        f.shortName.toLowerCase().includes(normFSearch) ||
        f.frameworkId.toLowerCase().includes(normFSearch) ||
        (JURISDICTION_LABELS[f.jurisdiction] ?? f.jurisdiction).toLowerCase().includes(normFSearch),
      );
    }
    return list;
  }, [allFrameworks, jurisdictions, normFSearch]);

  function toggle(arr: string[], v: string, set: (next: string[]) => void) {
    set(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
  }

  return (
    <AdminShell subtitle="Browse the Secure Controls Framework catalog. Filter by jurisdiction or framework, search within control text, and drill in for the full crosswalk.">
      <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Security Controls</h1>
        <p className="text-sm text-muted-foreground">
          Browse the Secure Controls Framework — {total.toLocaleString()} controls across {allFrameworks.length} compliance frameworks. Filter by jurisdiction or framework, then expand any control to see its full crosswalk.
          {version && <span className="ml-2 font-mono text-xs">SCF {version}</span>}
        </p>
      </div>

      {/* Filters — three independent cards, each with its own search.
          Mirrors the TEP settings tab pattern so users can find a chip
          quickly even with 44 jurisdictions and 250+ frameworks. */}

      {/* Jurisdictions */}
      <Card className="p-5 space-y-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Jurisdictions</p>
            <p className="text-xs text-muted-foreground">Filter frameworks below by region or country.</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {jurisdictions.length > 0 && (
              <button
                type="button"
                onClick={() => setJurisdictions([])}
                className="text-[11px] text-muted-foreground hover:text-foreground shrink-0"
              >
                clear ({jurisdictions.length})
              </button>
            )}
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="search"
                value={jSearch}
                onChange={(e) => setJSearch(e.target.value)}
                placeholder="Search jurisdictions…"
                className="w-full pl-8 pr-8 py-1.5 text-sm rounded-md border border-border bg-background"
              />
              {jSearch && (
                <button
                  type="button"
                  onClick={() => setJSearch('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  aria-label="Clear jurisdiction search"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        </div>
        {visibleJurisdictions.length === 0 ? (
          <p className="text-sm text-muted-foreground py-2">No jurisdictions match the search.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {visibleJurisdictions.map((j) => {
              const active = jurisdictions.includes(j);
              return (
                <button
                  key={j}
                  type="button"
                  onClick={() => toggle(jurisdictions, j, setJurisdictions)}
                  className={cn(
                    'px-3 py-1 rounded-full text-xs font-medium border transition-colors',
                    active
                      ? 'bg-primary/10 border-primary/40 text-primary'
                      : 'bg-card border-border text-muted-foreground hover:text-foreground',
                  )}
                >
                  {JURISDICTION_LABELS[j] ?? j.toUpperCase()}
                </button>
              );
            })}
          </div>
        )}
      </Card>

      {/* Frameworks */}
      <Card className="p-5 space-y-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Frameworks <span className="font-normal normal-case">({frameworks.length} selected)</span>
            </p>
            <p className="text-xs text-muted-foreground">Narrows the controls list to those mapped to any selected framework.</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {frameworks.length > 0 && (
              <button
                type="button"
                onClick={() => setFrameworks([])}
                className="text-[11px] text-muted-foreground hover:text-foreground shrink-0"
              >
                clear ({frameworks.length})
              </button>
            )}
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="search"
                value={fSearch}
                onChange={(e) => setFSearch(e.target.value)}
                placeholder="Search frameworks…"
                className="w-full pl-8 pr-8 py-1.5 text-sm rounded-md border border-border bg-background"
              />
              {fSearch && (
                <button
                  type="button"
                  onClick={() => setFSearch('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  aria-label="Clear framework search"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        </div>
        {visibleFrameworks.length === 0 ? (
          <p className="text-sm text-muted-foreground py-2">No frameworks match the current filters.</p>
        ) : (
          <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto">
            {visibleFrameworks.map((f) => {
              const active = frameworks.includes(f.frameworkId);
              return (
                <button
                  key={f.frameworkId}
                  type="button"
                  onClick={() => toggle(frameworks, f.frameworkId, setFrameworks)}
                  title={f.name}
                  className={cn(
                    'px-2 py-1 rounded text-[11px] font-medium border transition-colors',
                    active
                      ? 'bg-saffron/15 border-saffron/40 text-saffron'
                      : 'bg-card border-border text-muted-foreground hover:text-foreground',
                  )}
                >
                  {f.shortName}
                </button>
              );
            })}
          </div>
        )}
      </Card>

      {/* Controls search */}
      <Card className="p-5 space-y-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Controls</p>
          <p className="text-xs text-muted-foreground">Search within control id, name, domain, or description.</p>
        </div>
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search controls (e.g. SEA-04, MFA, encryption)…"
            className="w-full pl-8 pr-8 py-2 text-sm rounded-md border border-border bg-background"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              aria-label="Clear control search"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </Card>

      {/* Results */}
      {isLoading ? (
        <Card className="p-10 text-center text-sm text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin inline" />
        </Card>
      ) : controls.length === 0 ? (
        <Card className="p-10 text-center text-sm text-muted-foreground">No controls match the current filters.</Card>
      ) : (
        <Card className="p-0 overflow-hidden">
          <div className="px-5 py-3 border-b border-border flex items-center justify-between">
            <p className="text-sm font-semibold">{total.toLocaleString()} controls</p>
            {controls.length < total && (
              <p className="text-xs text-muted-foreground">Showing first {controls.length}. Refine filters or search to narrow further.</p>
            )}
          </div>
          <div className="divide-y divide-border">
            {controls.map((ctrl) => (
              <ControlRow
                key={ctrl.scfId}
                ctrl={ctrl}
                expanded={expandedId === ctrl.scfId}
                onToggle={() => setExpandedId(expandedId === ctrl.scfId ? null : ctrl.scfId)}
              />
            ))}
          </div>
        </Card>
      )}

      <p className="text-[11px] text-muted-foreground italic px-1">
        Catalog data is sourced from the <a href="https://securecontrolsframework.com" target="_blank" rel="noreferrer" className="underline">Secure Controls Framework</a>, used with attribution.
      </p>
      </div>
    </AdminShell>
  );
}

function ControlRow({ ctrl, expanded, onToggle }: { ctrl: Control; expanded: boolean; onToggle: () => void }) {
  return (
    <div className="px-5 py-3">
      <button type="button" onClick={onToggle} className="w-full flex items-start gap-3 text-left">
        <span className="mt-0.5 shrink-0">
          {expanded ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
        </span>
        <ShieldCheck className="h-4 w-4 mt-0.5 text-india-green shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-foreground">
            <span className="font-mono text-xs text-muted-foreground mr-2">{ctrl.scfId}</span>
            {ctrl.name}
          </p>
          <p className="text-xs text-muted-foreground">{ctrl.domain}</p>
        </div>
      </button>

      {expanded && <ControlDetail scfId={ctrl.scfId} description={ctrl.description} />}
    </div>
  );
}

function ControlDetail({ scfId, description }: { scfId: string; description: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['scf-catalog', 'control', scfId],
    queryFn: () => apiClient.getScfCatalogControl(scfId),
    staleTime: 5 * 60_000,
  });
  const detail = data?.data as { crosswalk?: CrosswalkEntry[] } | undefined;
  const crosswalk = detail?.crosswalk ?? [];

  // Group the crosswalk by jurisdiction so the visual reads as
  // "this control is reflected in these regimes worldwide".
  const grouped = useMemo(() => {
    const map = new Map<string, CrosswalkEntry[]>();
    for (const c of crosswalk) {
      if (!map.has(c.jurisdiction)) map.set(c.jurisdiction, []);
      map.get(c.jurisdiction)!.push(c);
    }
    return Array.from(map.entries()).sort((a, b) =>
      (JURISDICTION_LABELS[a[0]] ?? a[0]).localeCompare(JURISDICTION_LABELS[b[0]] ?? b[0]),
    );
  }, [crosswalk]);

  return (
    <div className="ml-10 mt-3 space-y-4">
      {description && (
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Description</p>
          <p className="text-sm text-foreground/90">{description}</p>
        </div>
      )}

      <div>
        <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">
          Crosswalk {!isLoading && crosswalk.length > 0 && (
            <span className="font-normal normal-case">({crosswalk.length} framework{crosswalk.length === 1 ? '' : 's'} reference this control)</span>
          )}
        </p>
        {isLoading ? (
          <p className="text-xs text-muted-foreground"><Loader2 className="h-3.5 w-3.5 inline animate-spin mr-1" /> Loading crosswalk…</p>
        ) : crosswalk.length === 0 ? (
          <p className="text-xs text-muted-foreground">No framework mappings recorded for this control.</p>
        ) : (
          <div className="space-y-3">
            {grouped.map(([jurisdiction, entries]) => (
              <div key={jurisdiction}>
                <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">
                  {JURISDICTION_LABELS[jurisdiction] ?? jurisdiction.toUpperCase()}
                </p>
                <div className="space-y-1.5">
                  {entries.map((e) => (
                    <div key={e.frameworkId} className="flex items-start gap-2 px-3 py-2 rounded-lg border border-border bg-muted/20">
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-semibold text-foreground">{e.shortName}</p>
                        <p className="text-[11px] text-muted-foreground line-clamp-1">{e.name}</p>
                        <div className="flex flex-wrap gap-1 mt-1.5">
                          {e.clauses.slice(0, 12).map((c, i) => (
                            <span
                              key={`${e.frameworkId}-${c.clauseId}-${i}`}
                              title={[c.clauseTitle, RELATIONSHIP_LABEL[c.relationship] ?? c.relationship].filter(Boolean).join(' · ')}
                              className="inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded bg-card border border-border text-foreground"
                            >
                              {c.clauseId}
                              {c.relationship !== 'intersects' && (
                                <span className="text-[9px] text-muted-foreground uppercase">
                                  {c.relationship === 'equal' ? '=' : c.relationship === 'subset' ? '⊂' : c.relationship === 'superset' ? '⊃' : ''}
                                </span>
                              )}
                            </span>
                          ))}
                          {e.clauses.length > 12 && (
                            <span className="text-[10px] text-muted-foreground">+{e.clauses.length - 12} more</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <p className="text-[11px] text-muted-foreground">
        SCF {scfId} <a href="https://securecontrolsframework.com" target="_blank" rel="noreferrer" className="ml-2 inline-flex items-center gap-1 underline">securecontrolsframework.com <ExternalLink className="h-3 w-3" /></a>
      </p>
    </div>
  );
}
